import * as React from 'react';
import moment from 'moment-timezone';
import * as DateUtil from '../../util/Date';
import { Callout, DirectionalHint, Icon, Panel, PanelType, TooltipHost, PrimaryButton, IObjectWithKey } from '@fluentui/react';
import { DailyShiftSchedule, ScheduleEdit, Shift, NotAllowedMatch } from '../../redux/scheduling/types';
import { AsyncData } from '../../redux/core';
import { DateSelector } from '../core/DateSelector';
import { HealAPI } from '../../healApi';
import { ShiftSubschedule } from './ShiftSubschedule';
import { ToastParams } from '../../redux/message/types';
import './Scheduling.scss';
import { PageHeader } from '../core/PageHeader';
import { PairingRulesSidePanel } from '../../components/core/PairingRulesSidePanel';
import { UIState } from '../../redux/ui/types';
import { Market } from '../../api/markets.types';

export interface StateProps {
  dailyShiftSchedule: AsyncData<DailyShiftSchedule>;
  date: string;
  marketGroups: string[];
  markets: Market[];
  pairingRules: AsyncData<NotAllowedMatch[]>;
  ui: UIState;
}

export interface DispatchProps {
  actions: {
    onPageDidMount: (name: string) => void;
    setDate: (date: string) => void;
    toast: (params: ToastParams) => void;
    setShowPairingRules: (show: boolean) => void;
    fetchPairingRules: () => void;
  };
}

type SchedulingProps = StateProps & DispatchProps;

export interface SchedulingState {
  highlightedShiftId: string;
  isLegendVisible: boolean;
  isRefreshingShifts: boolean;
  isSendingFloatMessages: boolean;
  isSendingShiftMessages: boolean;
  selectedShiftIds: string[];
  shouldShowEditHistory: boolean;
  selectedAmMAs?: string[];
  selectedPmMAs?: string[];
}

export class Scheduling extends React.Component<SchedulingProps, SchedulingState> {
  private legendIcon = React.createRef<HTMLDivElement>();
  private amSubschedule: ShiftSubschedule | null = null;
  private pmSubschedule: ShiftSubschedule | null = null;

  constructor(props: SchedulingProps) {
    super(props);

    this.state = {
      highlightedShiftId: '',
      isLegendVisible: false,
      isRefreshingShifts: false,
      isSendingFloatMessages: false,
      isSendingShiftMessages: false,
      selectedShiftIds: [],
      shouldShowEditHistory: false,
    };
  }

  public componentDidMount() {
    this.props.actions.onPageDidMount('Scheduling');
    this.props.actions.fetchPairingRules();
  }

  public componentDidUpdate(prevProps: SchedulingProps) {
    if (this.props.date !== prevProps.date) {
    }
  }

  public render(): JSX.Element {
    return (
      <div className={`scheduling ${this.selectedDateHasPassed() ? 'passed' : ''}`}>
        <PageHeader title="Scheduling" showShareButton={true} isLoading={this.props.dailyShiftSchedule.isFetching} />
        {this.renderEditLogs()}
        {this.renderLastRefresh()}
        <div className="flex">
          <DateSelector
            calendarProps={{
              maxDate: DateUtil.daysFromNow(90),
            }}
            defaultDate={this.props.date || DateUtil.daysFromNow(1)}
            displayFormat="dddd, MMMM D, YYYY"
            onChange={this.onSelectedDateChange}
          />
          <TooltipHost
            content={
              this.inRefreshTimeframe()
                ? 'Refresh shift data from WhenIWork. This action will not overwrite any changes. See above for the latest refresh for this day.'
                : 'Cannot refresh shifts in the past'
            }
          >
            <PrimaryButton className="refresh-shifts" disabled={this.shouldDisableShiftRefreshButton()} onClick={this.refreshShifts} text="Refresh shift schedule" />
          </TooltipHost>
          <TooltipHost content={this.messageButtonTooltipText()}>
            <PrimaryButton className="send-messages" disabled={this.shouldDisableShiftMessageButton()} onClick={this.sendShiftMessages} text="Send shift messages" />
          </TooltipHost>
          <TooltipHost content={this.floatMessageButtonTooltipText()}>
            <PrimaryButton className="send-messages" disabled={this.shouldDisableFloatMessageButton()} onClick={this.sendFloatMessages} text="Send float messages" />
          </TooltipHost>
          <TooltipHost content={'Manage not-allowed matches'}>
            <PrimaryButton className="manage-pairing-rules" onClick={this.managePairingRules} text="Manage not-allowed matches" />
          </TooltipHost>
          <TooltipHost content="Reload data. This is a faster alternative to refreshing the browser. This is different from Refresh Shift Schedule, which updates schedules based on the latest in WhenIWork.">
            <Icon className="reload-icon" iconName="Refresh" onClick={this.reloadShifts} />
          </TooltipHost>
          <div className="legend">
            <div
              className="icon"
              ref={this.legendIcon}
              onMouseEnter={() => this.setState({ isLegendVisible: true })}
              onMouseLeave={() => this.setState({ isLegendVisible: false })}
            >
              <Icon iconName="Info" />
            </div>
            <Callout className="legend-callout" directionalHint={DirectionalHint.rightTopEdge} hidden={!this.state.isLegendVisible} target={this.legendIcon.current}>
              <div className="key onsite-shift">Onsite shift</div>
              <div className="key nurse-shift">Nurse shift</div>
            </Callout>
          </div>
        </div>
        <TooltipHost content="Morning shifts" hostClassName="subschedule-header am">
          <Icon iconName="Sunny" className="icon" />
          <span className="title">AM</span>
        </TooltipHost>
        <ShiftSubschedule
          actions={{
            toast: this.props.actions.toast,
            onSelectionChanged: this.onShiftSelectionChanged,
          }}
          pairingRules={this.props.pairingRules}
          shiftType="am"
          allMarketGroupsSorted={this.props.marketGroups}
          markets={this.props.markets}
          highlightedShiftId={this.state.highlightedShiftId}
          isFetching={this.props.dailyShiftSchedule.isFetching}
          ref={(ref) => (this.amSubschedule = ref)}
          selectionEnabled={!this.shouldDisableShiftMessageButton()}
          subschedule={this.props.dailyShiftSchedule.data && this.props.dailyShiftSchedule.data.am}
          editHistory={this.props.dailyShiftSchedule.data && this.props.dailyShiftSchedule.data.editHistory}
          onSelectFloatMA={this.onSelectFloatMA}
          ui={this.props.ui}
        />
        <TooltipHost content="Afternoon shifts" hostClassName="subschedule-header pm">
          <Icon iconName="ClearNight" className="icon" />
          <span className="title">PM</span>
        </TooltipHost>
        <ShiftSubschedule
          actions={{
            toast: this.props.actions.toast,
            onSelectionChanged: this.onShiftSelectionChanged,
          }}
          pairingRules={this.props.pairingRules}
          shiftType="pm"
          allMarketGroupsSorted={this.props.marketGroups}
          markets={this.props.markets}
          highlightedShiftId={this.state.highlightedShiftId}
          isFetching={this.props.dailyShiftSchedule.isFetching}
          ref={(ref) => (this.pmSubschedule = ref)}
          selectionEnabled={!this.shouldDisableShiftMessageButton()}
          subschedule={this.props.dailyShiftSchedule.data && this.props.dailyShiftSchedule.data.pm}
          editHistory={this.props.dailyShiftSchedule.data && this.props.dailyShiftSchedule.data.editHistory}
          onSelectFloatMA={this.onSelectFloatMA}
          ui={this.props.ui}
        />
        <PairingRulesSidePanel />
      </div>
    );
  }

  private onSelectFloatMA = (MAs: IObjectWithKey[], shiftType: string) => {
    if (shiftType.toLowerCase() === 'am') {
      const selectedAmMAs = MAs.map((ma: any) => ma.id);
      this.setState({ selectedAmMAs });
    }
    if (shiftType.toLowerCase() === 'pm') {
      const selectedPmMAs = MAs.map((ma: any) => ma.id);
      this.setState({ selectedPmMAs });
    }
  };

  private inMessagingTimeframe(): boolean {
    const d = new Date(this.props.date);
    const now = new Date();
    // allow messaging if selected date is less than 24 hours ago, or less than 24 hours in the future
    // or if today is Friday and the selected date is the following Monday
    const within24Hours = DateUtil.isInRange(d, DateUtil.daysFromDate(now, -1), DateUtil.daysFromDate(now, 1));
    return within24Hours || (now.getDay() === 5 && d.getDay() === 1 && DateUtil.isInRange(d, now, DateUtil.daysFromDate(now, 4)));
  }

  private inRefreshTimeframe(): boolean {
    const d = new Date(this.props.date);
    // allow refresh if selected date is in the future or less than 24 hours ago
    return !DateUtil.isInPast(DateUtil.daysFromDate(d, 1));
  }

  private floatMessageButtonTooltipText(): string {
    const inMessagingTimeframe = this.inMessagingTimeframe();

    if (inMessagingTimeframe) {
      return 'Send messages to all float MAs';
    } else if (!inMessagingTimeframe) {
      return 'Can only send messages in 24 hours preceding shifts';
    }
    // NOTE if we track float MAs in this state we can change tooltip
    //  based on whether there are currently any floats
    return 'Must be at least one float MA to send';
  }

  private messageButtonTooltipText(): string {
    const inMessagingTimeframe = this.inMessagingTimeframe();

    if (inMessagingTimeframe) {
      return 'Send messages for selected shifts';
    } else if (!inMessagingTimeframe) {
      return 'Can only send messages in 24 hours preceding shifts';
    }
    return 'Must select at least one shift to send messages';
  }

  private renderLastRefresh(): JSX.Element {
    const refreshedAt: Date | null = this.props.dailyShiftSchedule.data ? this.props.dailyShiftSchedule.data.refreshedAt : null;
    if (refreshedAt) {
      return (
        <div className="last-refresh">
          <Icon className="refresh-icon" iconName="DelveAnalytics" />
          Last refreshed: <span className="timestamp">{moment.utc(refreshedAt).fromNow()}</span>
        </div>
      );
    } else {
      return (
        <div className="last-refresh no-refresh">
          <Icon className="refresh-icon" iconName="DelveAnalytics" />
          No known refreshes
        </div>
      );
    }
  }

  private renderEditLogs(): JSX.Element {
    const editLogs: ScheduleEdit[] = this.props.dailyShiftSchedule.data ? this.props.dailyShiftSchedule.data.editHistory : [];
    if (!editLogs || editLogs.length === 0) {
      return (
        <div className="history-preview no-history">
          <Icon className="history-icon" iconName="History" />
          No edits made
        </div>
      );
    }
    return (
      <div>
        <div
          className="history-preview"
          onClick={() => {
            this.setState({ shouldShowEditHistory: true });
          }}
          onMouseEnter={() => {
            this.setState({ highlightedShiftId: editLogs[0].shiftId });
          }}
          onMouseLeave={() => {
            this.setState({ highlightedShiftId: '' });
          }}
        >
          <Icon className="history-icon" iconName="History" />
          <a href={`mailto:${editLogs[0].author.email}`}>{editLogs[0].author.email.slice(0, -9)}</a>
          &nbsp;changed <b>{editLogs[0].fieldName}</b>
          &nbsp;from <i>{editLogs[0].fromValue}</i>
          &nbsp;to <i>{editLogs[0].toValue}</i>
          <span className="timestamp">({moment.utc(editLogs[0].date).fromNow()})</span>
        </div>

        <Panel
          className="edit-history"
          isBlocking={false}
          isOpen={this.state.shouldShowEditHistory}
          onDismiss={() => {
            this.setState({ shouldShowEditHistory: false });
          }}
          type={PanelType.medium}
          headerText="Edit History"
        >
          {editLogs.map((e) => {
            return (
              <div
                className="item"
                key={e.date.toString()}
                onMouseEnter={() => {
                  this.setState({ highlightedShiftId: e.shiftId });
                }}
                onMouseLeave={() => {
                  this.setState({ highlightedShiftId: '' });
                }}
              >
                <div>
                  {e.fieldName}: <i>{e.fromValue}</i> → <i>{e.toValue}</i>
                </div>
                <div className="author-line">
                  <a href={`mailto:${e.author.email}`}>{e.author.email.slice(0, -9)}</a> &bull; {moment.utc(e.date).fromNow()}
                </div>
              </div>
            );
          })}
        </Panel>
      </div>
    );
  }

  private selectedDateHasPassed(): boolean {
    const selectedDate = moment(this.props.date, 'l'); // 'l' === 7/21/2021
    const endOfSelectedDate = selectedDate.tz(moment.tz.guess()).endOf('day');
    return DateUtil.isInPast(endOfSelectedDate.toDate());
  }

  private shouldDisableFloatMessageButton(): boolean {
    // NOTE in future we could disable if a) outside timeframe or b) no floats exist
    return this.shouldDisableShiftMessageButton() || this.state.isSendingFloatMessages;
  }

  private shouldDisableShiftMessageButton(): boolean {
    return !this.inMessagingTimeframe() || this.state.isSendingShiftMessages; // TEMP
    // return !(this.inMessagingTimeframe() && this.hasSelectedShifts());
  }

  private shouldDisableShiftRefreshButton(): boolean {
    return !this.inRefreshTimeframe() || this.state.isRefreshingShifts;
  }

  private onSelectedDateChange = (newValue: string) => {
    this.props.actions.setDate(newValue);
  };

  private onShiftSelectionChanged = () => {
    const amSelection = this.amSubschedule ? this.amSubschedule.getSelection() : [];
    const pmSelection = this.pmSubschedule ? this.pmSubschedule.getSelection() : [];
    const selection = amSelection.concat(pmSelection);
    const shiftIds = selection.map((o) => {
      return (o as Shift).id;
    });
    this.setState({ selectedShiftIds: shiftIds });
  };

  private reloadShifts = () => {
    this.props.actions.setDate(this.props.date);
  };

  private refreshShifts = () => {
    this.setState({ isRefreshingShifts: true });
    HealAPI.refreshShiftsForDate(this.props.date)
      .then(() => {
        this.reloadShifts();
        this.props.actions.toast({
          type: 'info',
          title: 'Refresh Requested',
          content: 'Refreshing shifts will take some time, appreciate your patience. Reload the page after the refresh is complete.',
        });
        this.setState({ isRefreshingShifts: false });
      })
      .catch((e) => {
        this.props.actions.toast({ type: 'error', title: 'Error', content: e });
        this.setState({ isRefreshingShifts: false });
      });
  };

  private sendFloatMessages = () => {
    const amFloats = this.state.selectedAmMAs ? this.state.selectedAmMAs : [];
    const pmFloats = this.state.selectedPmMAs ? this.state.selectedPmMAs : [];

    if (amFloats.length === 0 && pmFloats.length === 0) {
      this.props.actions.toast({
        type: 'info',
        title: 'Cannot send',
        content: 'There are no selected float MAs for this date.',
      });
      return;
    }
    this.props.actions.toast({
      type: 'info',
      title: 'Sending Messages',
      content: 'Data will auto-reload upon completion.',
    });

    const promises: Promise<any>[] = [];

    if (amFloats.length > 0) {
      promises.push(HealAPI.sendFloatMessagesForDate(this.props.date, 'AM', amFloats));
    }
    if (pmFloats.length > 0) {
      promises.push(HealAPI.sendFloatMessagesForDate(this.props.date, 'PM', pmFloats));
    }

    // NOTE why does 404 not trigger error handler, but counts as success?
    this.setState({ isSendingFloatMessages: true });
    Promise.all(promises)
      .then(() => {
        this.reloadShifts();
        this.props.actions.toast({
          type: 'success',
          title: 'Sent',
          content: 'Sent messages to selected float MAs.',
        });
      })
      .catch((e) => this.props.actions.toast({ type: 'error', title: 'Error', content: e }))
      .finally(() => this.setState({ isSendingFloatMessages: false }));
  };

  private managePairingRules = () => {
    this.props.actions.setShowPairingRules(true);
  };

  private sendShiftMessages = () => {
    const amSelection = this.amSubschedule ? this.amSubschedule.getSelection() : [];
    const pmSelection = this.pmSubschedule ? this.pmSubschedule.getSelection() : [];
    const shiftIds = amSelection.concat(pmSelection).map((o) => {
      return (o as Shift).id;
    });
    if (shiftIds.length < 1) {
      this.props.actions.toast({
        type: 'info',
        title: 'Cannot send',
        content: 'Must select at least one shift to send messages.',
      });
      return;
    }
    this.props.actions.toast({
      type: 'info',
      title: 'Sending Messages',
      content: 'Data will auto-reload upon completion.',
    });
    this.setState({ isSendingShiftMessages: true });
    HealAPI.sendMessagesForShifts(shiftIds)
      .then(() => {
        this.reloadShifts();
        this.props.actions.toast({
          type: 'success',
          title: 'Sent',
          content: 'Sent messages for the selected shifts.',
        });
        this.setState({ isSendingShiftMessages: false });
      })
      .catch((e) => {
        this.props.actions.toast({ type: 'error', title: 'Error', content: e });
        this.setState({ isSendingShiftMessages: false });
      });
  };
}
